import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/GameConfig';
import { loadTheme, getTheme, UIBuilder, Theme } from '../ui/UITheme';
import { apiClient, User } from '../network/ApiClient';
import { AuthModal } from '../ui/AuthModal';

export class MenuScene extends Phaser.Scene {
    private ui!: UIBuilder;
    private theme!: Theme;
    private currentUser: User | null = null;
    private authContainer!: Phaser.GameObjects.Container;
    private authModal: AuthModal | null = null;

    // Accessible sizing constants
    private readonly AUTH_BUTTON_HEIGHT = 50;
    private readonly AUTH_BUTTON_PADDING = 24;  // Horizontal padding on each side
    private readonly AUTH_FONT_SIZE = 24;
    private readonly NAME_FONT_SIZE = 24;
    private readonly ELEMENT_GAP = 20;  // Gap between all elements

    constructor() {
        super({ key: 'MenuScene' });
    }

    async create(): Promise<void> {
        // Load theme first
        await loadTheme();
        this.theme = getTheme();
        this.ui = new UIBuilder(this);

        // Load user info
        await this.loadUserInfo();

        const { width, height } = this.cameras.main;

        // Draw background panel (full screen with bevel)
        const panelGraphics = this.add.graphics();
        this.ui.drawBeveledPanel(panelGraphics, 16, 16, width - 32, height - 32);

        // Auth UI in top-right corner
        this.createAuthUI();

        // Title
        const title = this.ui.createText(
            width / 2,
            height * 0.25,
            'VOID MATCH',
            'huge',
            'primary',
            true
        );
        title.setOrigin(0.5);

        // Subtitle
        const subtitle = this.ui.createText(
            width / 2,
            height * 0.35,
            'MATCH-3 ROGUE-LITE',
            'medium',
            'secondary'
        );
        subtitle.setOrigin(0.5);

        // Tagline
        const tagline = this.ui.createText(
            width / 2,
            height * 0.42,
            'Fit your ship. Match orbs. Survive the void.',
            'small',
            'muted'
        );
        tagline.setOrigin(0.5);

        // Large PLAY button - centered
        const playButtonWidth = 360;
        const playButtonHeight = 100;
        const playButtonY = height * 0.58;

        const playBg = this.add.graphics();
        playBg.fillStyle(this.theme.colors.buttonPrimary, 1);
        playBg.fillRoundedRect(-playButtonWidth / 2, -playButtonHeight / 2, playButtonWidth, playButtonHeight, 12);

        const playLabel = this.add.text(0, 0, 'PLAY', {
            fontFamily: 'monospace',
            fontSize: `${this.theme.fonts.sizeHuge}px`,
            color: '#ffffff',
            fontStyle: 'bold',
        });
        playLabel.setOrigin(0.5);

        const playButton = this.add.container(width / 2, playButtonY, [playBg, playLabel]);
        playButton.setSize(playButtonWidth, playButtonHeight);
        playButton.setInteractive({ useHandCursor: true });

        playButton.on('pointerover', () => {
            playBg.clear();
            playBg.fillStyle(this.theme.colors.buttonPrimary, 0.8);
            playBg.fillRoundedRect(-playButtonWidth / 2, -playButtonHeight / 2, playButtonWidth, playButtonHeight, 12);
        });

        playButton.on('pointerout', () => {
            playBg.clear();
            playBg.fillStyle(this.theme.colors.buttonPrimary, 1);
            playBg.fillRoundedRect(-playButtonWidth / 2, -playButtonHeight / 2, playButtonWidth, playButtonHeight, 12);
        });

        playButton.on('pointerdown', () => this.scene.start('BaseScene'));

        // Guest mode warning (only shown if not signed in)
        if (!this.currentUser) {
            const guestWarning = this.ui.createText(
                width / 2,
                playButtonY + playButtonHeight / 2 + 30,
                'Sign in to save your progress',
                'small',
                'muted'
            );
            guestWarning.setOrigin(0.5);
        }

        // Home button - lower left corner
        const homeButtonWidth = 140;
        const homeButtonHeight = 48;
        const homeButtonX = 50 + homeButtonWidth / 2;
        const homeButtonY = height - 50;

        const homeBg = this.add.graphics();
        homeBg.fillStyle(0x3a3a5e, 1);
        homeBg.fillRoundedRect(-homeButtonWidth / 2, -homeButtonHeight / 2, homeButtonWidth, homeButtonHeight, 8);

        const homeLabel = this.add.text(0, 0, 'HOME', {
            fontFamily: 'monospace',
            fontSize: '24px',
            color: '#ffffff',
            fontStyle: 'bold',
        });
        homeLabel.setOrigin(0.5);

        const homeButton = this.add.container(homeButtonX, homeButtonY, [homeBg, homeLabel]);
        homeButton.setSize(homeButtonWidth, homeButtonHeight);
        homeButton.setInteractive({ useHandCursor: true });

        homeButton.on('pointerover', () => {
            homeBg.clear();
            homeBg.fillStyle(0x3a3a5e, 0.7);
            homeBg.fillRoundedRect(-homeButtonWidth / 2, -homeButtonHeight / 2, homeButtonWidth, homeButtonHeight, 8);
        });

        homeButton.on('pointerout', () => {
            homeBg.clear();
            homeBg.fillStyle(0x3a3a5e, 1);
            homeBg.fillRoundedRect(-homeButtonWidth / 2, -homeButtonHeight / 2, homeButtonWidth, homeButtonHeight, 8);
        });

        homeButton.on('pointerdown', () => {
            window.location.href = '/';
        });

        // Version info
        const version = this.ui.createText(
            width / 2,
            height - 50,
            'v1.0.0',
            'label',
            'muted'
        );
        version.setOrigin(0.5);
    }

    private async loadUserInfo(): Promise<void> {
        try {
            this.currentUser = await apiClient.getMe();
        } catch (error) {
            console.error('Failed to load user info:', error);
            this.currentUser = null;
        }
    }

    private createAuthUI(): void {
        // Container at origin, elements positioned absolutely within
        this.authContainer = this.add.container(0, 50);
        this.authContainer.setDepth(500);
        this.updateAuthUI();
    }

    private updateAuthUI(): void {
        this.authContainer.removeAll(true);

        const { width } = this.cameras.main;
        const rightEdge = width - 40;
        let cursorX = rightEdge;  // Track position from right to left

        if (this.currentUser) {
            // Logged in: PlayerName [Change Name] [Sign Out]

            // Sign Out button (rightmost)
            const signOutBtn = this.createAuthButton('SIGN OUT', 0x8b3030, () => this.handleSignOut());
            const signOutWidth = (signOutBtn.getData('width') as number);
            cursorX -= signOutWidth / 2;
            signOutBtn.setPosition(cursorX, 0);
            cursorX -= signOutWidth / 2 + this.ELEMENT_GAP;
            this.authContainer.add(signOutBtn);

            // Change Name button
            const changeBtn = this.createAuthButton('CHANGE NAME', 0x3a3a5e, () => this.showChangeUsernameModal());
            const changeWidth = (changeBtn.getData('width') as number);
            cursorX -= changeWidth / 2;
            changeBtn.setPosition(cursorX, 0);
            cursorX -= changeWidth / 2 + this.ELEMENT_GAP;
            this.authContainer.add(changeBtn);

            // Display name
            const displayName = this.currentUser.displayName || 'Anonymous';
            const nameText = this.add.text(cursorX, 0, displayName, {
                fontFamily: 'monospace',
                fontSize: `${this.NAME_FONT_SIZE}px`,
                color: '#e8e8f0',
                fontStyle: 'bold',
            });
            nameText.setOrigin(1, 0.5);
            this.authContainer.add(nameText);
        } else {
            // Not logged in: Anonymous [Sign In]

            // Sign In button (rightmost)
            const signInBtn = this.createAuthButton('SIGN IN', 0x2ecc71, () => this.showSignInModal());
            const signInWidth = (signInBtn.getData('width') as number);
            cursorX -= signInWidth / 2;
            signInBtn.setPosition(cursorX, 0);
            cursorX -= signInWidth / 2 + this.ELEMENT_GAP;
            this.authContainer.add(signInBtn);

            // Anonymous text
            const anonText = this.add.text(cursorX, 0, 'Anonymous', {
                fontFamily: 'monospace',
                fontSize: `${this.NAME_FONT_SIZE}px`,
                color: '#606070',
            });
            anonText.setOrigin(1, 0.5);
            this.authContainer.add(anonText);
        }
    }

    /** Creates a button sized to fit its text label */
    private createAuthButton(text: string, color: number, callback: () => void): Phaser.GameObjects.Container {
        // Create label first to measure it
        const label = this.add.text(0, 0, text, {
            fontFamily: 'monospace',
            fontSize: `${this.AUTH_FONT_SIZE}px`,
            color: '#ffffff',
            fontStyle: 'bold',
        });
        label.setOrigin(0.5);

        // Size button based on text width + padding
        const w = Math.ceil(label.width) + this.AUTH_BUTTON_PADDING * 2;
        const h = this.AUTH_BUTTON_HEIGHT;

        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);

        const container = this.add.container(0, 0, [bg, label]);
        container.setSize(w, h);
        container.setData('width', w);  // Store width for layout calculations
        container.setInteractive({ useHandCursor: true });

        container.on('pointerover', () => {
            bg.clear();
            bg.fillStyle(color, 0.7);
            bg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
        });

        container.on('pointerout', () => {
            bg.clear();
            bg.fillStyle(color, 1);
            bg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
        });

        container.on('pointerdown', callback);

        return container;
    }

    private showSignInModal(): void {
        this.authModal = new AuthModal(this);
        this.authModal.show();
    }

    private handleSignOut(): void {
        // Use native browser confirm for simplicity (like ChemTD)
        const confirmed = window.confirm('Sign out of your account?');
        if (confirmed) {
            apiClient.logout().then(() => {
                this.currentUser = null;
                this.updateAuthUI();
            }).catch((error) => {
                console.error('Failed to sign out:', error);
            });
        }
    }

    private showChangeUsernameModal(): void {
        const { WIDTH, HEIGHT } = GAME_CONFIG;

        // Create modal overlay
        const overlay = this.add.graphics();
        overlay.fillStyle(0x000000, 0.7);
        overlay.fillRect(0, 0, WIDTH, HEIGHT);
        overlay.setInteractive(new Phaser.Geom.Rectangle(0, 0, WIDTH, HEIGHT), Phaser.Geom.Rectangle.Contains);
        overlay.setDepth(999);

        const modalWidth = 440;
        const modalHeight = 260;
        const inputWidth = modalWidth - 60;  // 30px padding each side
        const inputHeight = 48;
        const buttonWidth = 160;
        const buttonHeight = 50;
        const buttonGap = 20;

        const modalContainer = this.add.container(WIDTH / 2, HEIGHT / 2);
        modalContainer.setDepth(1000);

        // Modal background
        const bg = this.add.graphics();
        bg.fillStyle(0x1a1a2e, 1);
        bg.fillRoundedRect(-modalWidth / 2, -modalHeight / 2, modalWidth, modalHeight, 12);
        bg.lineStyle(2, 0x3a3a5e, 1);
        bg.strokeRoundedRect(-modalWidth / 2, -modalHeight / 2, modalWidth, modalHeight, 12);
        modalContainer.add(bg);

        // Title
        const title = this.add.text(0, -modalHeight / 2 + 40, 'CHANGE USERNAME', {
            fontFamily: 'monospace',
            fontSize: '28px',
            color: '#e8e8f0',
            fontStyle: 'bold',
        });
        title.setOrigin(0.5);
        modalContainer.add(title);

        // Get canvas position and scale for HTML input positioning
        const canvas = this.game.canvas;
        const canvasRect = canvas.getBoundingClientRect();
        const scaleX = canvasRect.width / WIDTH;
        const scaleY = canvasRect.height / HEIGHT;

        // Calculate input position in screen coordinates
        const inputScreenX = canvasRect.left + (WIDTH / 2) * scaleX;
        const inputScreenY = canvasRect.top + (HEIGHT / 2 - 20) * scaleY;  // Slightly above center
        const inputScreenWidth = inputWidth * scaleX;
        const inputScreenHeight = inputHeight * scaleY;

        // Create HTML input
        const input = document.createElement('input');
        input.type = 'text';
        input.value = this.currentUser?.displayName || '';
        input.maxLength = 50;
        input.placeholder = 'Enter username';
        input.style.cssText = `
            position: fixed;
            left: ${inputScreenX}px;
            top: ${inputScreenY}px;
            transform: translate(-50%, -50%);
            width: ${inputScreenWidth}px;
            height: ${inputScreenHeight}px;
            font-family: monospace;
            font-size: ${Math.floor(20 * scaleY)}px;
            padding: 0 ${12 * scaleX}px;
            border: 2px solid #3a3a5e;
            border-radius: ${6 * Math.min(scaleX, scaleY)}px;
            background: #0a0a12;
            color: #ffffff;
            outline: none;
            box-sizing: border-box;
        `;
        input.onfocus = () => input.style.borderColor = '#5a5a8e';
        input.onblur = () => input.style.borderColor = '#3a3a5e';
        document.body.appendChild(input);
        input.focus();
        input.select();

        const closeModal = () => {
            input.remove();
            overlay.destroy();
            modalContainer.destroy();
        };

        // Close button (X)
        const closeBtn = this.add.text(modalWidth / 2 - 30, -modalHeight / 2 + 30, 'X', {
            fontFamily: 'monospace',
            fontSize: '28px',
            color: '#606070',
            fontStyle: 'bold',
        });
        closeBtn.setOrigin(0.5);
        closeBtn.setInteractive({ useHandCursor: true });
        closeBtn.on('pointerover', () => closeBtn.setColor('#ffffff'));
        closeBtn.on('pointerout', () => closeBtn.setColor('#606070'));
        closeBtn.on('pointerdown', closeModal);
        modalContainer.add(closeBtn);

        // Buttons positioned at bottom of modal
        const buttonY = modalHeight / 2 - buttonHeight / 2 - 25;

        // Cancel button
        const cancelBtn = this.createModalButton(
            -(buttonWidth / 2 + buttonGap / 2), buttonY,
            'CANCEL',
            buttonWidth, buttonHeight,
            0x3a3a5e,
            closeModal
        );
        modalContainer.add(cancelBtn);

        // Save button
        const saveBtn = this.createModalButton(
            buttonWidth / 2 + buttonGap / 2, buttonY,
            'SAVE',
            buttonWidth, buttonHeight,
            0x2ecc71,
            async () => {
                const newName = input.value.trim();
                if (newName.length < 1 || newName.length > 50) {
                    input.style.borderColor = '#e74c3c';
                    return;
                }

                try {
                    await apiClient.updateDisplayName(newName);
                    if (this.currentUser) {
                        this.currentUser.displayName = newName;
                    }
                    this.updateAuthUI();
                    closeModal();
                } catch (error) {
                    console.error('Failed to update username:', error);
                    input.style.borderColor = '#e74c3c';
                }
            }
        );
        modalContainer.add(saveBtn);

        // Handle enter key
        input.onkeydown = (e) => {
            if (e.key === 'Enter') {
                (saveBtn as any).emit('pointerdown');
            } else if (e.key === 'Escape') {
                closeModal();
            }
        };

        // Click overlay to close
        overlay.on('pointerdown', closeModal);
    }

    private createModalButton(
        x: number, y: number, text: string, width: number, height: number, color: number, callback: () => void
    ): Phaser.GameObjects.Container {
        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);

        const label = this.add.text(0, 0, text, {
            fontFamily: 'monospace',
            fontSize: '24px',
            color: '#ffffff',
            fontStyle: 'bold',
        });
        label.setOrigin(0.5);

        const container = this.add.container(x, y, [bg, label]);
        container.setSize(width, height);
        container.setInteractive({ useHandCursor: true });

        container.on('pointerover', () => {
            bg.clear();
            bg.fillStyle(color, 0.7);
            bg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);
        });

        container.on('pointerout', () => {
            bg.clear();
            bg.fillStyle(color, 1);
            bg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);
        });

        container.on('pointerdown', callback);

        return container;
    }
}
